import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaArrowUp, FaThumbsUp, FaShareSquare } from "react-icons/fa";
import CustomShareButton from "../CustomShareButton/CustomShareButton";
import DarkMode from "../../../shared/components/DarkMode/DarkMode";
import supabase from "../../../../supabaseClient";
import CommentSection from "../CommentSection/CommentSection";
import { Helmet } from "react-helmet";
import "./BlogPost.css";

function BlogPost({ blogContent }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchBlog = useCallback(async () => {
    let { data: blog, error } = await supabase
      .from("blogs")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching blog:", error);
    } else {
      // Dynamically generate the thumbnail URL
      const thumbnailURL = `https://oginzzlmzlozxkihihtv.supabase.co/storage/v1/object/public/blog-thumbnails/${blog.id}.webp`;
      setBlog({ ...blog, thumbnail: thumbnailURL });
    }
  }, [id]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  const handleBackClick = () => {
    navigate("/blog");
  };

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShareClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleLike = async () => {
    if (blog) {
      let { error } = await supabase
        .from("blogs")
        .update({ likes: blog.likes + 1 })
        .eq("id", blog.id);

      if (error) {
        console.error("Error updating like count:", error);
      } else {
        setBlog({ ...blog, likes: blog.likes + 1 });
      }
    }
  };

  const formatContent = (content) => {
    return content.split("\n").map((paragraph, index) => <p key={index}>{paragraph}</p>);
  };

  if (!blog) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
      </div>
    );
  }

  // Format the date
  const formattedDate = new Date(blog.date).toLocaleDateString("en-GB", {
    month: "long",
    day: "2-digit",
  });

  // Prepare the truncated content for sharing
  const truncatedDescription = blog.content.slice(0, 150) + "...";

  return (
    <>
      {/* Helmet for dynamic meta tags */}
      <Helmet>
        <meta property="og:title" content={blog.title || "Default Blog Title"} />
        <meta property="og:description" content={truncatedDescription} />
        <meta property="og:image" content={blog.thumbnail} />
        <meta property="og:url" content={`https://reformedmissionalchurch.org/#/blog/${blog.id}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title || "Default Blog Title"} />
        <meta name="twitter:description" content={truncatedDescription} />
        <meta name="twitter:image" content={blog.thumbnail} />
      </Helmet>

      <Container>
        <Row>
          <Col md={10} lg={12} sm={10} xs={10} className="mx-auto">
            <div className="darkmode">
              <DarkMode />
            </div>
            <div className="icon-buttons">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="tooltip-back">Go Back</Tooltip>}
              >
                <div
                  className="d-flex align-items-center"
                  onClick={handleBackClick}
                  style={{ cursor: "pointer" }}
                >
                  <FaArrowLeft className="me-2" />
                </div>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="tooltip-share">Share</Tooltip>}
              >
                <div
                  className="d-flex align-items-center"
                  onClick={handleShareClick}
                  style={{ cursor: "pointer" }}
                >
                  <FaShareSquare className="me-2 share-button" />
                </div>
              </OverlayTrigger>
            </div>
            <p className="date">{formattedDate}</p>
            <h2 className="post-title">{blog.title}</h2>
            <img
              src={blog.thumbnail}
              alt={blog.title}
              className="img-fluid blog-thumbnail mb-4"
            />
            <div className="blog-content">{formatContent(blog.content)}</div>
            <Row className="like-comment-section align-items-center">
              <Col md={4} className="d-flex align-items-center">
                <FaThumbsUp
                  className="me-2 like-button"
                  onClick={handleLike}
                  style={{ cursor: "pointer" }}
                />
                <span className="me-4">{blog.likes}</span>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="tooltip-share">Share this post</Tooltip>}
                >
                  <div
                    className="d-flex align-items-center"
                    onClick={handleShareClick}
                    style={{ cursor: "pointer" }}
                  >
                    <FaShareSquare className="share-button" />
                  </div>
                </OverlayTrigger>
              </Col>
              <Col md={8}>
                <CommentSection postId={blog.id} />
              </Col>
            </Row>
            <div className="back-to-top top-icon">
              <FaArrowUp onClick={handleScrollTop} />
            </div>
          </Col>
        </Row>
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Share This Post</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CustomShareButton
              url={window.location.href}
              title={blog.title}
              image={blog.thumbnail}
              description={truncatedDescription}
              date={formattedDate}
              content={blog.content}
              blogId={id}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

export default BlogPost;
